import { useEffect, useState } from "react";
import { useFetcher, useNavigate, useSearchParams } from "react-router-dom";
import RouteNames from "../../routes/type";
import { BackButton, Button } from "../../components";
import { classNames } from "../../helper";
import { claimStepsData } from "./types";
import { PiCheckBold } from "react-icons/pi";
import SelectItems from "./SelectItems";
import ItemDetailes from "./ItemDetailes";
import { FormProvider, useForm } from "react-hook-form";
import { connect, useDispatch } from "react-redux";
import {
  fetchClaimInfo,
  insertClaimItemDamages,
  insertClaimItems,
  submitClaim,
} from "../../store/claim/actions";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import { RootState } from "../../store/root-reducer";
import { ClaimScope } from "../../store/claim/types";
import queryString from "query-string";
import Loader from "../../components/Loader";
import ReportDamage from "./ReportDamage";
import { fetchOptions } from "../../store/user/actions";
import ReviewClaim from "./ReviewClaim";
import SubmitClaim from "./SubmitClaim";
import { useTranslation } from "react-i18next";
import { getScopeError } from "../../store/scope-error/helpers";
import LinkNotValid from "../../components/LinkNotValid";
import moment from "moment";
import { nextStepRedirect } from "../../store/claim/helpers";
import { UserScope } from "../../store/user/types";

const lastStepNum = claimStepsData.length;
const sucessStepNum = lastStepNum + 1;

function CreateClaim({ loading, error, warranty_items, plan }) {
  let [searchParams] = useSearchParams();
  const formMethods = useForm({
    defaultValues: {
      selectedPlan: "",
      selectedItems: [],
      claimItems: {},
      claimItemDamage: [],
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
    getValues,
  } = formMethods;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stepId: number = parseInt(searchParams.get("step") || "");
  const claimId: string = searchParams.get("claimId") || "";
  const warranty_id: string = searchParams.get("warranty_id") || "";

  useEffect(() => {
    if (+stepId == 1) {
      navigate(`/${RouteNames.SELECT_PLAN}`, { replace: true });
    } else if (
      (!stepId && !/^[0-9]*$/.test(`${stepId}`)) ||
      stepId > sucessStepNum
    ) {
      nextStepRedirect(2);
    }
    return () => {};
  }, [navigate, stepId]);

  useEffect(() => {
    dispatch(fetchOptions());
    return () => {};
  }, [dispatch]);

  useEffect(() => {
    if (claimId && warranty_id && !plan.id) {
      dispatch(
        fetchClaimInfo({
          claimId,
          warranty_id,
        })
      );
    }
    return () => {};
  }, [claimId, dispatch, plan.id, warranty_id]);

  const currentStep = claimStepsData.find((item) => item.step_num === stepId);

  const navigateToNextStep = (step) => {
    const parsed = queryString.parse(window.location.search);
    navigate(
      `/${RouteNames.CREATE_CLAIM}?${queryString.stringify({
        ...parsed,
        step: step ?? stepId + 1,
      })}`
    );
  };
  const handleNext = () => {
    const values = getValues();
    switch (stepId) {
      case 2:
        navigateToNextStep(3);
        break;

      case 3:
        const mappedData = [];
        for (const key in values.claimItems) {
          const element = values.claimItems[key];
          const currntWarrantyItem =
            warranty_items?.find((item) => item.trackKey === key) || {};
          mappedData.push({
            ...(element.claimItemId
              ? { claimItemId: +element.claimItemId }
              : {}),
            manuFacturer: +element.manufacturer,
            selectedItemId: currntWarrantyItem.id,
            material: +element.material,
            color: +element.color,
            itemType: +currntWarrantyItem.type.id,
          });
        }
        const payload = {
          warrentyId: warranty_id,
          claimId,
          claim_items: mappedData,
        };
        dispatch(insertClaimItems(payload));
        break;

      case 4:
        const mappedDamagesData = [];
        for (const key in values.claimItemDamage) {
          const element = values.claimItemDamage[key] || [];

          element.forEach((item) => {
            mappedDamagesData.push({
              ...(item?.damageId ? { damageId: item.damageId } : {}),
              selectedItemId: key,
              damageType: item?.damage_category,
              specificDamage: item?.specific_damage,
              specificLocation: item?.damage_area,
              damageDate: item?.when_did_you_notice_the_damage
                ? moment(item?.when_did_you_notice_the_damage).format(
                    "YYYY-MM-DD"
                  )
                : null,
              howDidOccur: item?.how_did_it_happen,
              actionTaken: item?.did_you_take_any_steps_to_remedy,
              notes: item?.note,
            });
          });
        }

        const apiBody = {
          claimId: claimId,
          customerId: plan.customerId,
          damage_item: mappedDamagesData,
        };
        dispatch(insertClaimItemDamages(apiBody));
        break;

      case 5:
        dispatch(submitClaim());
        break;

      default:
        break;
    }
  };

  const stepComponents = {
    2: <SelectItems key="stepId2" />,
    3: <ItemDetailes key="stepId3" />,
    4: <ReportDamage key="stepId4" />,
    5: <ReviewClaim key="stepId5" />,
  };

  if (error === "CLAIM_NOT_FOUND") {
    return <LinkNotValid />;
  }

  if (stepId === sucessStepNum) {
    return (
      <Loader isLoading={loading}>
        <SubmitClaim />
      </Loader>
    );
  }

  return (
    <div className="px-4 space-y-3">
      <div className="mt-4 flex justify-between items-center">
        <BackButton
          onClick={() => {
            if (stepId === 2) {
              navigate(
                `/${RouteNames.SELECT_PLAN}?warranty_id=${warranty_id}&claimId=${claimId}`
              );
            } else if (stepId === 1) {
              navigate(`/${RouteNames.SELECT_SEARCH_METHOD}`);
            } else {
              navigateToNextStep(stepId - 1);
            }
          }}
        />
        <div className="flex flex-row gap-2">
          {claimStepsData.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA] "
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      <form onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>
          {stepComponents[stepId] || null}
        </FormProvider>
        <Button
          className="my-4"
          submit
          isLoading={loading}
          disabled={!(stepId === 4 || isValid)}
        >
          {stepId === lastStepNum ? t("Submit") : t("Next")}
        </Button>
      </form>
    </div>
  );
}
const mapStateToProps = (state: RootState) => ({
  loading:
    isScopeLoading(state.scopeLoading, ClaimScope) ||
    isScopeLoading(state.scopeLoading, UserScope),
  warranty_items: state?.claim?.warranty_items || [],
  error: getScopeError(state.scopeError, ClaimScope),
  plan: state.claim.plan,
});

export default connect(mapStateToProps)(CreateClaim);
